import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import clsx from "clsx";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { InvoiceDraftContextButtons } from "@/pages/dokumente/InvoiceDraftContextButtons";
import { InvoiceDraftContextModalsProvider } from "@/pages/dokumente/invoiceDraftContextModals";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useInAppWindowFocus } from "@/lib/inAppWindowFocus";
import {
  useCurrentUser,
  userHasGroup,
  userIsAbrechnung,
  userIsBuero,
  userIsManagement,
} from "@/lib/auth";
import {
  adoptReplacedPositionIds,
  buildPositionBillingBadgeMap,
  canCreateDocumentBillingQuestions,
  canRespondDocumentBillingQuestions,
  canViewDocumentBillingAnswers,
  listDokumentBillingQuestions,
  matchPositionForTimesheetMaterial,
  mergedChildrenSignature,
  remapDokumentBillingQuestionPositionIds,
  type DokumentBillingQuestion,
} from "@/lib/dokumentBilling";
import {
  getTimesheetDetail,
  listBillingQuestions,
  type BillingQuestion,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  buildMergedLeistungFromPositions,
  getMaterialMergedChildren,
} from "@/features/timesheetFlow/billingAnswerPositionUtils";
import { DocumentBillingQuestionCreateDialog } from "@/pages/dokumente/DocumentBillingQuestionCreateDialog";
import { DocumentBillingAnswersDialog } from "@/pages/dokumente/DocumentBillingAnswersDialog";
import { readGdMeta } from "@/pages/dokumente/gdPositionMeta";
import { calcDocument, formatCurrency, groupTaxByRate } from "@/lib/documentCalc";
import {
  getAngebotsanfrageByGdAngebot,
  type AgaByGdAngebotRef,
} from "@/lib/angebotsanfragen";
import { toast } from "@/lib/toast";
import { ensureDokumentStandardtexte } from "@/lib/gdStandardtexte";
import { ExistingLieferscheinDialog } from "@/pages/dokumente/ExistingLieferscheinDialog";
import {
  loadExistingLieferscheine,
  type ExistingLieferscheinHint,
} from "@/pages/dokumente/existingLieferscheinHint";
import { AngebotDialog } from "@/pages/angebotsanfragen/AngebotDialog";
import {
  CONVERSION_TARGETS,
  convertDokument,
  canBillingReopenDokument,
  canConvertDokument,
  deleteDokument,
  DOKUMENT_STATUS_BADGE,
  DOKUMENT_STATUS_LABELS,
  DOKUMENT_TYP_LABELS,
  getDokumentTypLabel,
  applyRechnungsadresseToEmpfaenger,
  canCreateInvoiceDraftFromCancelled,
  canDownloadDokumentPdf,
  canRepairStornoPositionen,
  canReopenDokumentForEdit,
  canSendDokumentMail,
  createDokumentRevision,
  createInvoiceDraftFromCancelled,
  documentChainStepPdfVariant,
  dokumentDisplayNumber,
  dokumentDisplayTitle,
  fetchDocumentChain,
  fetchDokumentXrechnung,
  listLinkedAuftragsbestaetigungen,
  mapPdfOpenVariantToPrintVariant,
  openDokumentPdf,
  openPdfAtApiUrl,
  filterGdLohnartenForUser,
  getAuftragQuelle,
  getDokument,
  getGdLohnarten,
  isDokumentEditable,
  prefetchKundenStundenArtikel,
  repairStornoPositionen,
  showsDokumentPdfView,
  updateDokument,
  updateDokumentStatus,
  usesDokumentRevisionForReopen,
  type DokumentDetail,
  type DokumentStatus,
  type DokumentTyp,
  type DocumentChain,
  type GdLohnart,
} from "@/lib/dokumente";
import { AngebotAngenommenDialog } from "@/pages/dokumente/AngebotAngenommenDialog";
import { BillingReopenDialog } from "@/pages/dokumente/BillingReopenDialog";
import {
  DocumentBodyTextModal,
  type BodyTextKind,
} from "@/pages/dokumente/DocumentBodyTextModal";
import { DocumentHeaderModal, type HeaderEditSection } from "@/pages/dokumente/DocumentHeaderModal";
import {
  DocumentReferenzenDialog,
  DocumentVerknuepfungenBlock,
} from "@/pages/dokumente/DocumentReferenzenDialog";
import {
  DocumentFooterSection,
  DocumentHeaderSection,
  DocumentIntroSection,
} from "@/pages/dokumente/DocumentHeaderSection";
import { DocumentInvoiceContextPanel } from "@/pages/dokumente/DocumentInvoiceContextPanel";
import { DocumentMahnungPanel } from "@/pages/dokumente/DocumentMahnungPanel";
import { DocumentMailDialog } from "@/pages/dokumente/DocumentMailDialog";
import { DocumentPaymentsDialog } from "@/pages/dokumente/DocumentPaymentsDialog";
import { mahnungPdfUrl } from "@/lib/mahnwesen";
import { DocumentChainPanel } from "@/pages/dokumente/DocumentChainPanel";
import {
  Exc2KalkulationHinweisAlert,
  isProjectExc2,
  loadExc2Answer,
  saveExc2Answer,
  type Exc2Choice,
} from "@/pages/dokumente/Exc2KalkulationHinweis";
import { DocumentPdfEmbed } from "@/pages/dokumente/DocumentPdfEmbed";
import { getOrder } from "@/lib/projectOverview";
import { patchProjectExecutionClass } from "@/lib/projects";
import {
  DocumentPositionCanvas,
  DocumentPositionCanvasPane,
  DocumentPositionCanvasSidebar,
} from "@/pages/dokumente/DocumentPositionCanvas";
import { DocumentPositionEditDialog } from "@/pages/dokumente/DocumentPositionEditDialog";
import { DocumentMergedPositionEditDialog } from "@/pages/dokumente/DocumentMergedPositionEditDialog";
import { DocumentMobileUploadDialog } from "@/pages/dokumente/DocumentMobileUploadDialog";
import { DocumentPreviewDialog } from "@/pages/dokumente/DocumentPreviewDialog";
import { DokumentPrintAction } from "@/pages/dokumente/DokumentPrintAction";
import { DocumentSaveTemplateDialog } from "@/pages/dokumente/DocumentSaveTemplateDialog";
import { DocumentTemplateEditorDialog } from "@/pages/dokumente/DocumentTemplateEditorDialog";
import { ConfirmActionDialog } from "@/pages/montageplanung/ConfirmActionDialog";
import {
  deleteGdVorlage,
  getGdVorlage,
  listGdVorlagen,
  reorderGdVorlagen,
} from "@/lib/gdVorlagen";
import {
  applyDocumentTemplate,
  type GdDocumentTemplateListItem,
} from "@/pages/dokumente/documentTemplateModel";
import {
  ensurePositionMediaKey,
  extractMergedChildFromPositions,
  isMergedContainerPosition,
  linkStrukturTitelLocalIds,
  mergePositionsAfterSave,
  normalizePositionTyp,
  positionDisplayTitle,
  positionTotals,
  syncLeistungEinzelpreis,
  toEditorPosition,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";
import { buildPositionDisplayNumbers } from "@/pages/dokumente/documentPositionLayout";
import {
  buildEditorSnapshot,
  editorHasUnsavedChanges,
} from "@/pages/dokumente/documentEditorSnapshot";
import { DocumentSaveStatus } from "@/pages/dokumente/DocumentSaveStatus";
import { useDocumentEditorLayers } from "@/pages/dokumente/documentEditorLayers";
import { useAuftragsbestaetigungFromAngebot } from "@/pages/dokumente/useAuftragsbestaetigungFromAngebot";
import {
  useDokumentAutoSave,
  type DokumentAutoSaveResult,
} from "@/pages/dokumente/useDokumentAutoSave";
import { useProvisionalPositionEditor } from "@/pages/dokumente/useProvisionalPositionEditor";
import { useRealtime } from "@/lib/useRealtime";
import "@/pages/dokumente/dokumente.css";

type ZeroEuroPositionInfo = {
  localId: string;
  displayNumber: string | null;
  title: string;
};

/** Preisrelevante Positionen (Artikel/Leistung/Nachweis) mit Zeilensumme 0 €. */
function collectZeroEuroPositions(positions: EditorPosition[]): ZeroEuroPositionInfo[] {
  const displayNumbers = buildPositionDisplayNumbers(positions);
  const out: ZeroEuroPositionInfo[] = [];
  for (let i = 0; i < positions.length; i++) {
    const row = positions[i];
    if (!row) continue;
    const typ = normalizePositionTyp(row.position_typ);
    if (typ === "titel" || typ === "text") continue;
    const pricedRow = typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
    if (Math.abs(positionTotals(pricedRow).zeilen_netto) >= 0.005) continue;
    out.push({
      localId: row.localId,
      displayNumber: displayNumbers[i] ?? null,
      title: positionDisplayTitle(row),
    });
  }
  return out;
}

export interface DocumentEditorDialogProps {
  open: boolean;
  docId: number | null;
  onClose: () => void;
  onDeleted?: () => void;
  onDocIdChange?: (docId: number) => void;
}

export function DocumentEditorDialog({
  open,
  docId,
  onClose,
  onDeleted,
  onDocIdChange,
}: DocumentEditorDialogProps) {
  const { user } = useCurrentUser();
  const { focusSurface } = useInAppWindowFocus();
  const layers = useDocumentEditorLayers();
  const { open: openAuftragsbestaetigungFromAngebot } = useAuftragsbestaetigungFromAngebot();
  const canProcessAga = useMemo(() => {
    if (!user) return false;
    return (
      userHasGroup(user, "App-Admins") ||
      userIsBuero(user) ||
      userIsManagement(user)
    );
  }, [user]);

  const [doc, setDoc] = useState<DokumentDetail | null>(null);
  const [positions, setPositions] = useState<EditorPosition[]>([]);
  const [lohnarten, setLohnarten] = useState<GdLohnart[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [headerSection, setHeaderSection] = useState<HeaderEditSection | null>(null);
  const [referenzenOpen, setReferenzenOpen] = useState(false);
  const [bodyTextKind, setBodyTextKind] = useState<BodyTextKind | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [chainTabKey, setChainTabKey] = useState<string>("");
  const [chain, setChain] = useState<DocumentChain | null>(null);
  const {
    editLocalId,
    editPosition,
    openPosition,
    closePositionEditor,
    savePosition,
  } = useProvisionalPositionEditor(positions, setPositions);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [draftFromCancelledConfirmOpen, setDraftFromCancelledConfirmOpen] = useState(false);
  const [importingHeader, setImportingHeader] = useState(false);
  const [templates, setTemplates] = useState<GdDocumentTemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [applyingTemplateId, setApplyingTemplateId] = useState<number | null>(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [editTemplateId, setEditTemplateId] = useState<number | null>(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState<number | null>(null);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [billingReopenOpen, setBillingReopenOpen] = useState(false);
  const [billingCreateOpen, setBillingCreateOpen] = useState(false);
  const [billingAnswersOpen, setBillingAnswersOpen] = useState(false);
  const [dokumentBillingQuestions, setDokumentBillingQuestions] = useState<
    DokumentBillingQuestion[]
  >([]);
  const [timesheetBillingQuestions, setTimesheetBillingQuestions] = useState<BillingQuestion[]>([]);
  const [timesheetBillingMaterials, setTimesheetBillingMaterials] = useState<
    TimesheetMaterialRecord[]
  >([]);
  const [mobileUploadOpen, setMobileUploadOpen] = useState(false);
  const [attachmentsRevision, setAttachmentsRevision] = useState(0);
  const [linkedAga, setLinkedAga] = useState<AgaByGdAngebotRef | null>(null);
  const [agaDialogOpen, setAgaDialogOpen] = useState(false);
  const [angenommenDialogOpen, setAngenommenDialogOpen] = useState(false);
  const [projectIsExc2, setProjectIsExc2] = useState(false);
  const [exc2Choice, setExc2Choice] = useState<Exc2Choice | null>(null);
  const [exc2Loading, setExc2Loading] = useState(false);
  const [exc2Saving, setExc2Saving] = useState(false);
  const [zeroEuroWarningOpen, setZeroEuroWarningOpen] = useState(false);
  const [lieferscheinConflictOpen, setLieferscheinConflictOpen] = useState(false);
  const [existingLieferscheine, setExistingLieferscheine] = useState<ExistingLieferscheinHint[]>(
    [],
  );
  const [zeroEuroWarningPositions, setZeroEuroWarningPositions] = useState<
    ZeroEuroPositionInfo[]
  >([]);
  const [invoiceDetailView, setInvoiceDetailView] = useState<"pdf" | "kalkulation">("pdf");
  const skipZeroEuroWarningRef = useRef(false);
  const openMailAfterCreateRef = useRef(false);

  const editable = doc ? isDokumentEditable(doc) : false;
  const pdfView = doc ? showsDokumentPdfView(doc) : false;
  const canReopen = doc ? canReopenDokumentForEdit(doc) : false;
  const canDraftFromCancelled = doc ? canCreateInvoiceDraftFromCancelled(doc) : false;
  const canRepairStorno = Boolean(
    doc && user && userIsBuero(user) && canRepairStornoPositionen(doc, positions),
  );
  const canBillingReopen =
    Boolean(doc && user && userIsBuero(user) && canBillingReopenDokument(doc));
  const canCreateBillingQ = canCreateDocumentBillingQuestions(user);
  const canViewBillingAnswers = canViewDocumentBillingAnswers(user);
  const canRespondBillingQ = canRespondDocumentBillingQuestions(user);
  const showBillingAnswersButton =
    canViewBillingAnswers &&
    (userIsAbrechnung(user) || canRespondBillingQ || dokumentBillingQuestions.length > 0);
  const pdfEnabled = doc ? canDownloadDokumentPdf(doc) : false;
  const currentChainKey = doc ? `${doc.dokument_typ}:${doc.id}` : "";
  const showChainTabs = Boolean(chain && chain.steps.length > 1);
  const viewingOwnDocument = !chainTabKey || chainTabKey === currentChainKey;
  const activeChainStep = useMemo(() => {
    if (!chain) return null;
    return chain.steps.find((item) => item.key === (chainTabKey || chain.current_key)) ?? null;
  }, [chain, chainTabKey]);
  const printTarget = useMemo(() => {
    if (!doc || !pdfView || !pdfEnabled) return null;
    const step = activeChainStep;
    if (step?.kind === "stundennachweis" || step?.kind === "mahnung") return null;
    const docId = step?.doc_id ?? doc.id;
    if (!docId) return null;
    return {
      docId,
      variant: mapPdfOpenVariantToPrintVariant(
        step?.kind === "kalkulation"
          ? "kalkulation"
          : step?.kind === "beauftragung"
            ? "beauftragung"
            : "angebot",
      ),
    };
  }, [activeChainStep, doc, pdfEnabled, pdfView]);
  const isDraft = doc?.status === "entwurf";
  const visibleLohnarten = useMemo(
    () => filterGdLohnartenForUser(lohnarten, user),
    [lohnarten, user],
  );
  const isRechnung = doc?.dokument_typ === "rechnung";
  const showInvoiceContext = Boolean(
    isRechnung && doc.auftrag_id != null && Number(doc.auftrag_id) > 0,
  );
  const exc2Answered = Boolean(
    doc?.dokument_typ === "angebot" &&
      doc.auftrag_id &&
      (projectIsExc2 || exc2Choice === "ja" || exc2Choice === "nein"),
  );
  const requiresExc2Answer = Boolean(
    isDraft &&
      editable &&
      doc?.dokument_typ === "angebot" &&
      doc.auftrag_id &&
      (exc2Loading || !exc2Answered),
  );
  const showExc2KalkulationHinweis = Boolean(
    doc?.dokument_typ === "angebot" && (projectIsExc2 || exc2Choice === "ja"),
  );
  const exc2KalkulationHinweis = showExc2KalkulationHinweis ? (
    <Exc2KalkulationHinweisAlert className="mb-4" />
  ) : null;

  const resetState = useCallback(() => {
    setDoc(null);
    setPositions([]);
    setLohnarten([]);
    setLoading(false);
    setSaving(false);
    setError(null);
    setSavedSnapshot(null);
    setHeaderSection(null);
    setReferenzenOpen(false);
    setPreviewOpen(false);
    setPdfLoading(false);
    setChainTabKey("");
    setChain(null);
    closePositionEditor();
    setReopenConfirmOpen(false);
    setDraftFromCancelledConfirmOpen(false);
    setImportingHeader(false);
    setTemplates([]);
    setTemplatesLoading(false);
    setApplyingTemplateId(null);
    setSaveTemplateOpen(false);
    setDeleteTemplateId(null);
    setPaymentsOpen(false);
    setBillingReopenOpen(false);
    setBillingCreateOpen(false);
    setBillingAnswersOpen(false);
    setDokumentBillingQuestions([]);
    setTimesheetBillingQuestions([]);
    setTimesheetBillingMaterials([]);
    setLinkedAga(null);
    setAgaDialogOpen(false);
    setInvoiceDetailView("pdf");
    setProjectIsExc2(false);
    setExc2Choice(null);
    setExc2Loading(false);
    setExc2Saving(false);
  }, [closePositionEditor]);

  const reloadBillingQuestions = useCallback(async () => {
    if (!open || docId == null || !Number.isFinite(docId)) return;
    if (!canViewBillingAnswers && !canCreateBillingQ && !canRespondBillingQ) {
      setDokumentBillingQuestions([]);
      setTimesheetBillingQuestions([]);
      setTimesheetBillingMaterials([]);
      return;
    }
    try {
      const docQs = await listDokumentBillingQuestions(docId, { includeAnswered: true }).catch(
        () => [] as DokumentBillingQuestion[],
      );
      setDokumentBillingQuestions(docQs);

      const timesheetIds = (chain?.steps || [])
        .filter((step) => step.kind === "stundennachweis" && step.timesheet_id != null)
        .map((step) => Number(step.timesheet_id))
        .filter((id) => Number.isFinite(id) && id > 0);

      if (!timesheetIds.length) {
        setTimesheetBillingQuestions([]);
        setTimesheetBillingMaterials([]);
        return;
      }

      const bundles = await Promise.all(
        timesheetIds.map(async (tid) => {
          const [questions, detail] = await Promise.all([
            listBillingQuestions(tid, { includeAnswered: true }).catch(() => [] as BillingQuestion[]),
            getTimesheetDetail(tid).catch(() => null),
          ]);
          return {
            questions,
            materials: detail?.materials ?? ([] as TimesheetMaterialRecord[]),
          };
        }),
      );
      setTimesheetBillingQuestions(bundles.flatMap((b) => b.questions));
      setTimesheetBillingMaterials(bundles.flatMap((b) => b.materials));
    } catch {
      setDokumentBillingQuestions([]);
      setTimesheetBillingQuestions([]);
      setTimesheetBillingMaterials([]);
    }
  }, [
    open,
    docId,
    chain,
    canViewBillingAnswers,
    canCreateBillingQ,
    canRespondBillingQ,
  ]);

  useEffect(() => {
    void reloadBillingQuestions();
  }, [reloadBillingQuestions]);

  const billingBadgeByLocalId = useMemo(
    () =>
      buildPositionBillingBadgeMap({
        dokumentQuestions: dokumentBillingQuestions,
        timesheetQuestions: timesheetBillingQuestions,
        timesheetMaterials: timesheetBillingMaterials,
        positions,
      }),
    [
      dokumentBillingQuestions,
      timesheetBillingQuestions,
      timesheetBillingMaterials,
      positions,
    ],
  );

  const billingUpdateCount = useMemo(() => {
    let count = 0;
    const seen = new Set<number>();
    for (const material of timesheetBillingMaterials) {
      const mid = Number(material.id);
      if (!Number.isFinite(mid) || seen.has(mid)) continue;
      const children = getMaterialMergedChildren(material);
      if (!children.length) continue;
      const pos = matchPositionForTimesheetMaterial(positions, material, mid);
      if (!pos) continue;
      const current = readGdMeta(pos.kalkulation_json).merged_children || [];
      if (
        mergedChildrenSignature(children) !==
        mergedChildrenSignature(
          current.map((c) => ({
            bezeichnung: c.bezeichnung,
            menge: c.menge,
            einheit: c.einheit,
            einzelpreis_netto: c.einzelpreis_netto,
          })),
        )
      ) {
        seen.add(mid);
        count += 1;
      }
    }
    return count;
  }, [timesheetBillingMaterials, positions]);

  const applyBillingChildren = useCallback(
    (
      localId: string,
      children: EditorPosition[],
      options?: { timesheetMaterialPositionId?: number | null },
    ) => {
      if (!children.length) return;
      setPositions((prev) =>
        prev.map((row) => {
          if (row.localId !== localId) return row;
          const existingMaterialId = readGdMeta(row.kalkulation_json).timesheet_material_position_id;
          const materialId =
            options?.timesheetMaterialPositionId != null
              ? Number(options.timesheetMaterialPositionId)
              : existingMaterialId;
          const merged = buildMergedLeistungFromPositions(children, row.bezeichnung);
          let next: EditorPosition = {
            ...row,
            position_typ: "leistung",
            einzelpreis_netto: merged.einzelpreis_netto,
            einheit: merged.einheit || row.einheit || "pauschal",
            kalkulation_json: merged.kalkulation_json,
          };
          if (materialId != null && Number.isFinite(materialId)) {
            const stamped = { ...(next.kalkulation_json as Record<string, unknown>) };
            stamped._gd_meta = {
              ...((stamped._gd_meta as Record<string, unknown>) || {}),
              timesheet_material_position_id: materialId,
            };
            next = {
              ...next,
              kalkulation_json: stamped as EditorPosition["kalkulation_json"],
            };
          }
          return next;
        }),
      );
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const reload = useCallback(async (opts?: { silent?: boolean }) => {
    if (!open || docId == null || !Number.isFinite(docId)) return;
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [detail, lohnartenList, chainData] = await Promise.all([
        getDokument(docId),
        getGdLohnarten(),
        fetchDocumentChain(docId).catch(() => null),
      ]);
      const resolvedBestellnummer =
        detail.bestellnummer?.trim() ||
        chainData?.bestellnummer?.trim() ||
        detail.auftrag_bestellnummer?.trim() ||
        "";
      const mergedDetail = resolvedBestellnummer
        ? { ...detail, bestellnummer: resolvedBestellnummer }
        : detail;
      setDoc(mergedDetail);
      setLohnarten(lohnartenList);
      setChain(chainData);
      setChainTabKey((prev) =>
        silent && prev
          ? prev
          : chainData?.current_key || `${mergedDetail.dokument_typ}:${mergedDetail.id}`,
      );
      setPositions((prev) => {
        // Stilles Nachladen: localIds / offene Dialoge behalten
        const next =
          silent && prev.length > 0
            ? mergePositionsAfterSave(prev, detail.positionen || [])
            : linkStrukturTitelLocalIds(
                (detail.positionen || []).map((p, idx) => toEditorPosition(p, idx)),
              );
        setSavedSnapshot(buildEditorSnapshot(mergedDetail, next));
        return next;
      });
      // Stunden-Artikel früh vorladen (gleiche Quelle wie Empfänger/Kunde am Dokument)
      prefetchKundenStundenArtikel(mergedDetail.kunden_id ?? null, {
        auftragId: mergedDetail.auftrag_id ?? null,
      });
    } catch (err) {
      if (!silent) {
        setError(err instanceof ApiError ? err.message : "Dokument konnte nicht geladen werden.");
        setDoc(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [docId, open]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    setInvoiceDetailView("pdf");
    closePositionEditor();
  }, [docId, closePositionEditor]);

  useEffect(() => {
    if (!open || !doc || doc.dokument_typ !== "angebot") {
      setLinkedAga(null);
      return;
    }
    let cancelled = false;
    void getAngebotsanfrageByGdAngebot(doc.id)
      .then((ref) => {
        if (!cancelled) setLinkedAga(ref);
      })
      .catch(() => {
        if (!cancelled) setLinkedAga(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, doc?.id, doc?.dokument_typ]);

  useEffect(() => {
    if (!open || !doc || doc.dokument_typ !== "angebot" || !doc.auftrag_id) {
      setProjectIsExc2(false);
      setExc2Choice(null);
      setExc2Loading(false);
      return;
    }
    let cancelled = false;
    setExc2Loading(true);
    const stored = loadExc2Answer(doc.id);
    void getOrder(doc.auftrag_id)
      .then((order) => {
        if (cancelled) return;
        const already = isProjectExc2(order.execution_class);
        setProjectIsExc2(already);
        setExc2Choice(already ? "ja" : stored);
      })
      .catch(() => {
        if (cancelled) return;
        setProjectIsExc2(false);
        setExc2Choice(stored);
      })
      .finally(() => {
        if (!cancelled) setExc2Loading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, doc?.id, doc?.dokument_typ, doc?.auftrag_id]);

  const chainPanel = chain ? (
    <DocumentChainPanel
      chain={chain}
      activeKey={chainTabKey || chain.current_key}
      onTabChange={setChainTabKey}
      currentDocId={doc?.id ?? null}
      onOpenDocument={onDocIdChange}
      onDeleteDraft={(id) => void handleDelete(id)}
      zIndexClass={layers.overlay}
    />
  ) : null;

  const reloadTemplates = useCallback(async () => {
    if (!open || !doc?.dokument_typ) return;
    setTemplatesLoading(true);
    try {
      const list = await listGdVorlagen({
        typ: doc.dokument_typ,
        auftragId: doc.auftrag_id ?? null,
      });
      setTemplates(list || []);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [doc?.auftrag_id, doc?.dokument_typ, open]);

  useEffect(() => {
    void reloadTemplates();
  }, [reloadTemplates]);

  const handleApplyTemplate = useCallback(
    async (templateId: number, replace: boolean, insertAt?: number) => {
      if (!doc || !editable) return;
      setApplyingTemplateId(templateId);
      setError(null);
      try {
        const template = await getGdVorlage(templateId);
        const { positions: nextPositions, headerPatch } = applyDocumentTemplate(
          positions,
          template,
          replace ? "replace" : "append",
          replace,
          insertAt,
        );
        setPositions(nextPositions);
        if (headerPatch && Object.keys(headerPatch).length > 0) {
          setDoc((prev) => (prev ? { ...prev, ...headerPatch } : prev));
        }
        toast.success(
          replace
            ? `Vorlage „${template.name}“ angewendet.`
            : `Vorlage „${template.name}“ eingefügt.`,
        );
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Vorlage konnte nicht angewendet werden.");
      } finally {
        setApplyingTemplateId(null);
      }
    },
    [doc, editable, positions],
  );

  const handleDeleteTemplate = useCallback((templateId: number) => {
    setDeleteTemplateId(templateId);
  }, []);

  const handleEditTemplate = useCallback((templateId: number) => {
    setEditTemplateId(templateId);
  }, []);

  const handleReorderTemplates = useCallback(
    async (orderedIds: number[]) => {
      const previous = templates;
      const byId = new Map(templates.map((item) => [item.id, item]));
      const next = orderedIds
        .map((id, index) => {
          const item = byId.get(id);
          return item ? { ...item, sort_index: index } : null;
        })
        .filter((item): item is GdDocumentTemplateListItem => item != null);
      setTemplates(next);
      try {
        await reorderGdVorlagen(orderedIds.map((id, index) => ({ id, sort_index: index })));
      } catch (err) {
        setTemplates(previous);
        setError(err instanceof ApiError ? err.message : "Vorlagen-Reihenfolge konnte nicht gespeichert werden.");
      }
    },
    [templates],
  );

  const confirmDeleteTemplate = useCallback(async () => {
    if (deleteTemplateId == null) return;
    try {
      await deleteGdVorlage(deleteTemplateId);
      toast.success("Vorlage gelöscht.");
      await reloadTemplates();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vorlage konnte nicht gelöscht werden.");
    } finally {
      setDeleteTemplateId(null);
    }
  }, [deleteTemplateId, reloadTemplates]);

  // Echo der eigenen Autosave-Events kurz unterdrücken (handleSaved hat die Daten schon).
  const ignoreRealtimeUntilRef = useRef(0);
  const headerSectionRef = useRef(headerSection);
  headerSectionRef.current = headerSection;
  const referenzenOpenRef = useRef(referenzenOpen);
  referenzenOpenRef.current = referenzenOpen;

  const handleSaved = useCallback(
    (updated: DokumentDetail, result?: DokumentAutoSaveResult) => {
      ignoreRealtimeUntilRef.current = Date.now() + 2000;
      // Während der Empfänger-Bearbeitung lokale Tippeingaben nicht durch die
      // Autosave-Antwort überschreiben (z. B. Adresszusatz).
      const preserveEmpfaenger = headerSectionRef.current === "empfaenger";
      const serverPositions = updated.positionen || [];
      setDoc((prev) => {
        if (!prev) return updated;
        if (result?.stale) {
          // Während des Speicherns wurde weiter editiert: lokale Kopfdaten behalten,
          // nur Versionsstand übernehmen – der Follow-up-Save schreibt die Edits nach.
          return { ...prev, version: updated.version, updated_at: updated.updated_at };
        }
        if (!preserveEmpfaenger) return updated;
        return {
          ...updated,
          kunde_name: prev.kunde_name,
          kunde_adresszusatz: prev.kunde_adresszusatz,
          kunde_strasse: prev.kunde_strasse,
          kunde_hausnummer: prev.kunde_hausnummer,
          kunde_plz: prev.kunde_plz,
          kunde_ort: prev.kunde_ort,
          kunde_email: prev.kunde_email,
          kunde_telefon: prev.kunde_telefon,
          kundennummer: prev.kundennummer,
          ansprechpartner_id: prev.ansprechpartner_id,
          ansprechpartner_name: prev.ansprechpartner_name,
          ansprechpartner_email: prev.ansprechpartner_email,
          ansprechpartner_telefon: prev.ansprechpartner_telefon,
          rechnungsadresse_id: prev.rechnungsadresse_id,
        };
      });
      if (result?.savedPositionIds) {
        const previousIds = result.savedPositionIds;
        setDokumentBillingQuestions((questions) =>
          remapDokumentBillingQuestionPositionIds(
            questions,
            previousIds,
            serverPositions.map((position) => position.id ?? null),
          ),
        );
      }
      // Veraltete Save-Antwort (z. B. Merge während laufendem Autosave): Inhalt
      // behalten, aber die neuen Positions-IDs übernehmen – sonst verliert die
      // nächste Speicherung die Rückfrage-Markierung.
      if (result?.stale) {
        if (result.savedLocalIds) {
          const savedLocalIds = result.savedLocalIds;
          setPositions((prev) => adoptReplacedPositionIds(prev, savedLocalIds, serverPositions));
        }
        return;
      }
      setPositions((prev) => {
        const next = mergePositionsAfterSave(prev, serverPositions);
        // Snapshot = Serverstand, damit lokale Empfänger-Edits als dirty bleiben und nachgespeichert werden.
        setSavedSnapshot(buildEditorSnapshot(updated, next));
        return next;
      });
    },
    [],
  );

  const hasUnsavedChanges = useMemo(
    () => editorHasUnsavedChanges(doc, positions, savedSnapshot, editable),
    [doc, positions, savedSnapshot, editable],
  );

  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const editLocalIdRef = useRef(editLocalId);
  editLocalIdRef.current = editLocalId;
  const bodyTextKindRef = useRef(bodyTextKind);
  bodyTextKindRef.current = bodyTextKind;

  const { saveState, flushSave, retrySave, lastSavedAt, saveError } = useDokumentAutoSave({
    doc,
    positions,
    dirty: hasUnsavedChanges,
    editable,
    onSaved: handleSaved,
  });

  const saveStateRef = useRef(saveState);
  saveStateRef.current = saveState;

  useRealtime({
    scopes:
      docId != null && Number.isFinite(docId)
        ? [`dokumente:${docId}`, "dokumente:list"]
        : ["dokumente:list"],
    domains: ["dokumente", "hero"],
    events: ["dokumente_changed", "dokument_status_changed", "hero_changed"],
    enabled: open && docId != null && Number.isFinite(docId),
    onChange: () => {
      // Eigenes Autosave / offene Positionsbearbeitung nicht durch Voll-Reload zerstören
      if (Date.now() < ignoreRealtimeUntilRef.current) return;
      if (hasUnsavedChangesRef.current) return;
      if (editLocalIdRef.current != null) return;
      if (headerSectionRef.current != null) return;
      if (referenzenOpenRef.current) return;
      if (bodyTextKindRef.current != null) return;
      if (saveStateRef.current === "pending" || saveStateRef.current === "saving") return;
      void reload({ silent: true });
    },
  });

  const previewTotals = useMemo(() => calcDocument(positions), [positions]);
  const taxGroups = useMemo(() => groupTaxByRate(positions), [positions]);
  const isAutoSaving = saveState === "pending" || saveState === "saving";
  const closeGuardDirty = hasUnsavedChanges && saveState !== "saving";

  const conversionOptions = useMemo(() => {
    if (!doc || !canConvertDokument(doc)) return [] as DokumentTyp[];
    return CONVERSION_TARGETS[doc.dokument_typ] || [];
  }, [doc]);

  const canCreateAuftragsbestaetigungFromAngebot = useMemo(() => {
    if (!doc || doc.dokument_typ !== "angebot" || doc.status !== "angenommen") return false;
    return listLinkedAuftragsbestaetigungen(doc.verknuepfungen || [], doc.id).length === 0;
  }, [doc]);

  function applyAcceptedAngebot(updated: DokumentDetail) {
    setDoc(updated);
    setPositions((prev) => {
      const nextPositions = mergePositionsAfterSave(prev, updated.positionen || []);
      setSavedSnapshot(buildEditorSnapshot(updated, nextPositions));
      return nextPositions;
    });
    // Bestellnummer-Dialog erst nach Leave-Transition des Angenommen-Modals
    window.setTimeout(() => {
      openAuftragsbestaetigungFromAngebot({ angebotId: updated.id });
    }, 180);
  }

  async function handleExc2Choice(choice: Exc2Choice) {
    if (!doc?.auftrag_id || !editable) return;
    setExc2Saving(true);
    setError(null);
    try {
      if (choice === "ja" && !projectIsExc2) {
        await patchProjectExecutionClass(doc.auftrag_id, "EXC2");
        setProjectIsExc2(true);
      }
      setExc2Choice(choice);
      saveExc2Answer(doc.id, choice);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "EXC2-Markierung konnte nicht gesetzt werden.",
      );
    } finally {
      setExc2Saving(false);
    }
  }

  async function handleStatus(next: DokumentStatus): Promise<boolean> {
    if (!doc) return false;
    if (next === "angenommen" && doc.dokument_typ === "angebot") {
      setAngenommenDialogOpen(true);
      return false;
    }
    if (
      next === "erstellt" &&
      doc.dokument_typ === "angebot" &&
      doc.auftrag_id &&
      !projectIsExc2 &&
      exc2Choice !== "ja" &&
      exc2Choice !== "nein"
    ) {
      setError("Bitte unter Kopfdaten angeben, ob es ein EXC2-Projekt ist.");
      return false;
    }
    if (
      next === "erstellt" &&
      doc.dokument_typ === "rechnung" &&
      !skipZeroEuroWarningRef.current
    ) {
      const zeros = collectZeroEuroPositions(positions);
      if (zeros.length > 0) {
        setZeroEuroWarningPositions(zeros);
        setZeroEuroWarningOpen(true);
        return false;
      }
    }
    skipZeroEuroWarningRef.current = false;
    if (next === "erstellt" && editable) {
      // Immer durchspeichern (nicht nur bei `hasUnsavedChanges`): der Wert ist im
      // Closure ggf. veraltet und ein laufender Autosave könnte noch nicht durch sein.
      // Das fertige PDF wird aus dem Serverstand erzeugt – ohne bestätigten Save
      // würden Positionen aus der Vorschau im finalen Dokument fehlen.
      const saved = await flushSave();
      if (!saved) {
        openMailAfterCreateRef.current = false;
        const msg =
          "Änderungen konnten nicht gespeichert werden – das Dokument wurde nicht erstellt. Bitte erneut versuchen.";
        setError(msg);
        toast.error(msg);
        return false;
      }
    }
    setSaving(true);
    try {
      if (
        next === "erstellt" &&
        doc.dokument_typ === "angebot" &&
        doc.auftrag_id &&
        exc2Choice === "ja" &&
        !projectIsExc2
      ) {
        await patchProjectExecutionClass(doc.auftrag_id, "EXC2");
        setProjectIsExc2(true);
      }
      const updated = await updateDokumentStatus(doc.id, next);
      const storno = updated.storno_dokument;
      if (next === "storniert" && storno?.id) {
        const nextPositions = linkStrukturTitelLocalIds(
          (storno.positionen || []).map((p, idx) => toEditorPosition(p, idx)),
        );
        setDoc(storno);
        setPositions(nextPositions);
        setSavedSnapshot(buildEditorSnapshot(storno, nextPositions));
        setChainTabKey(`${storno.dokument_typ}:${storno.id}`);
        const stornoNr = dokumentDisplayNumber(storno);
        toast.success(
          stornoNr
            ? `Stornorechnung ${stornoNr} erstellt.`
            : "Stornorechnung erstellt.",
        );
        onDocIdChange?.(storno.id);
      } else {
        setDoc(updated);
        setPositions((prev) => {
          const nextPositions = mergePositionsAfterSave(prev, updated.positionen || []);
          setSavedSnapshot(buildEditorSnapshot(updated, nextPositions));
          return nextPositions;
        });
        toast.success(`Status: ${DOKUMENT_STATUS_LABELS[next]}`);
      }
      return true;
    } catch (err) {
      openMailAfterCreateRef.current = false;
      setError(err instanceof ApiError ? err.message : "Statuswechsel fehlgeschlagen.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateAndSend() {
    openMailAfterCreateRef.current = true;
    const ok = await handleStatus("erstellt");
    if (ok) {
      openMailAfterCreateRef.current = false;
      setMailOpen(true);
    }
  }

  async function confirmZeroEuroCreate() {
    setZeroEuroWarningOpen(false);
    skipZeroEuroWarningRef.current = true;
    const openMail = openMailAfterCreateRef.current;
    openMailAfterCreateRef.current = false;
    const ok = await handleStatus("erstellt");
    if (ok && openMail) setMailOpen(true);
  }

  function cancelZeroEuroCreate() {
    setZeroEuroWarningOpen(false);
    openMailAfterCreateRef.current = false;
  }

  async function handleConvert(zielTyp: DokumentTyp, options?: { forceNew?: boolean }) {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      if (
        zielTyp === "lieferschein" &&
        !options?.forceNew &&
        doc.auftrag_id != null &&
        Number.isFinite(Number(doc.auftrag_id))
      ) {
        const existing = await loadExistingLieferscheine(Number(doc.auftrag_id));
        if (existing.length) {
          setExistingLieferscheine(existing);
          setLieferscheinConflictOpen(true);
          return;
        }
      }

      const created = await ensureDokumentStandardtexte(await convertDokument(doc.id, zielTyp));
      setLieferscheinConflictOpen(false);
      const nextPositions = linkStrukturTitelLocalIds(
        (created.positionen || []).map((p, idx) => toEditorPosition(p, idx)),
      );
      setDoc(created);
      setPositions(nextPositions);
      setSavedSnapshot(buildEditorSnapshot(created, nextPositions));
      toast.success(`${DOKUMENT_TYP_LABELS[zielTyp]} erstellt.`);
      onDocIdChange?.(created.id);
    } catch (err) {
      setLieferscheinConflictOpen(false);
      setError(err instanceof ApiError ? err.message : "Umwandlung fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(targetId?: number) {
    const id = targetId ?? doc?.id;
    if (!id) return;
    const isCurrent = doc?.id === id;
    const revision = isCurrent ? Number(doc?.revision_index || 0) : 0;
    const message =
      revision > 0
        ? "Nur diese Entwurfsversion löschen? Die vorherige Version und das Projekt bleiben erhalten."
        : "Diesen Entwurf löschen? Das Projekt bleibt erhalten.";
    if (!window.confirm(message)) return;
    setSaving(true);
    try {
      await deleteDokument(id);
      toast.success("Entwurf gelöscht.");
      if (isCurrent) {
        onDeleted?.();
        onClose();
        return;
      }
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function confirmReopenForEdit() {
    if (!doc || !canReopen) return;
    setReopenConfirmOpen(false);
    if (usesDokumentRevisionForReopen(doc)) {
      setSaving(true);
      setError(null);
      try {
        const created = await createDokumentRevision(doc.id);
        const chainData = await fetchDocumentChain(created.id).catch(() => null);
        const nextPositions = linkStrukturTitelLocalIds(
          (created.positionen || []).map((p, idx) => toEditorPosition(p, idx)),
        );
        // Editor sofort auf den neuen Entwurf umschalten (nicht erst auf Parent-Reload warten)
        setDoc(created);
        setPositions(nextPositions);
        setSavedSnapshot(buildEditorSnapshot(created, nextPositions));
        setChain(chainData);
        setChainTabKey(chainData?.current_key || `${created.dokument_typ}:${created.id}`);
        toast.success(
          created.display_number
            ? `Neue Version ${created.display_number} angelegt.`
            : "Neue Angebots-Version angelegt.",
        );
        onDocIdChange?.(created.id);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Neue Version konnte nicht angelegt werden.");
      } finally {
        setSaving(false);
      }
      return;
    }
    await handleStatus("entwurf");
  }

  async function confirmDraftFromCancelled() {
    if (!doc || !canDraftFromCancelled) return;
    setDraftFromCancelledConfirmOpen(false);
    setSaving(true);
    setError(null);
    try {
      const created = await createInvoiceDraftFromCancelled(doc.id);
      const chainData = await fetchDocumentChain(created.id).catch(() => null);
      const nextPositions = linkStrukturTitelLocalIds(
        (created.positionen || []).map((p, idx) => toEditorPosition(p, idx)),
      );
      setDoc(created);
      setPositions(nextPositions);
      setSavedSnapshot(buildEditorSnapshot(created, nextPositions));
      setChain(chainData);
      setChainTabKey(chainData?.current_key || `${created.dokument_typ}:${created.id}`);
      toast.success("Neuer Rechnungsentwurf angelegt.");
      onDocIdChange?.(created.id);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Rechnungsentwurf konnte nicht angelegt werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleRepairStornoPositionen() {
    if (!doc || !canRepairStorno) return;
    setSaving(true);
    setError(null);
    try {
      const repaired = await repairStornoPositionen(doc.id);
      const chainData = await fetchDocumentChain(repaired.id).catch(() => null);
      const nextPositions = linkStrukturTitelLocalIds(
        (repaired.positionen || []).map((p, idx) => toEditorPosition(p, idx)),
      );
      setDoc(repaired);
      setPositions(nextPositions);
      setSavedSnapshot(buildEditorSnapshot(repaired, nextPositions));
      if (chainData) {
        setChain(chainData);
        setChainTabKey(chainData.current_key || `${repaired.dokument_typ}:${repaired.id}`);
      }
      const count = Number(repaired.repaired_position_count || nextPositions.length || 0);
      toast.success(
        count > 0
          ? `${count} Position(en) nachgezogen – Nummer ${dokumentDisplayNumber(repaired)} unverändert.`
          : "Storno-Positionen nachgezogen.",
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Positionen konnten nicht nachgezogen werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleOpenPdf() {
    if (!doc || !pdfEnabled || !chain) return;
    const step = chain.steps.find((item) => item.key === (chainTabKey || chain.current_key));
    if (!step) return;
    setPdfLoading(true);
    setError(null);
    try {
      if (step.kind === "stundennachweis" && step.timesheet_id) {
        setError("Stundennachweis-PDF bitte direkt im Tab ansehen oder herunterladen.");
        return;
      }
      if (step.kind === "mahnung" && step.mahnung_id) {
        const name = [step.label, step.dokument_nummer].filter(Boolean).join(" ");
        await openPdfAtApiUrl(mahnungPdfUrl(step.mahnung_id), name ? `${name}.pdf` : "Mahnung.pdf");
        return;
      }
      if (!step.doc_id) return;
      const variant = documentChainStepPdfVariant(step);
      const nummer = dokumentDisplayNumber(doc);
      const hint =
        variant === "kalkulation"
          ? `${nummer}_Kalkulation.pdf`
          : variant === "beauftragung"
            ? `${nummer}_Beauftragung.pdf`
            : variant === "signed"
              ? `${nummer}_unterschrieben.pdf`
              : `${nummer}.pdf`;
      await openDokumentPdf(step.doc_id, { variant, filenameHint: hint });
    } catch (err) {
      setError(err instanceof Error ? err.message : "PDF konnte nicht erzeugt werden.");
    } finally {
      setPdfLoading(false);
    }
  }

  async function handleOpenXrechnung() {
    if (!doc?.hat_xrechnung_xml) return;
    setPdfLoading(true);
    setError(null);
    try {
      const blob = await fetchDokumentXrechnung(doc.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${dokumentDisplayNumber(doc)}.xml`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "X-Rechnung konnte nicht heruntergeladen werden.");
    } finally {
      setPdfLoading(false);
    }
  }

  async function handleResetCreatorContact() {
    if (!doc || !editable) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateDokument(doc.id, {
        version: doc.version,
        created_by_name: null,
        created_by_email: null,
        created_by_telefon: null,
        created_by_mobil: null,
      });
      setDoc(updated);
      setSavedSnapshot(buildEditorSnapshot(updated, positions));
      toast.success("Ersteller-Kontakt wiederhergestellt.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kontaktdaten konnten nicht zurückgesetzt werden.");
    } finally {
      setSaving(false);
    }
  }

  function patchDoc(partial: Partial<DokumentDetail>) {
    setDoc((prev) => (prev ? { ...prev, ...partial } : prev));
    if (partial.reverse_charge_13b === true) {
      // § 13b: Positionen sofort auch clientseitig auf 0 % USt setzen –
      // der Server erzwingt dies beim Speichern ebenfalls.
      setPositions((prev) =>
        prev.map((p) => (p.steuersatz_prozent !== 0 ? { ...p, steuersatz_prozent: 0 } : p)),
      );
    }
  }

  async function handleImportHeaderFromProject() {
    if (!doc?.auftrag_id || !editable) return;
    setImportingHeader(true);
    setError(null);
    try {
      const quelle = await getAuftragQuelle(doc.auftrag_id);
      const patch: Partial<DokumentDetail> = {
        kunden_id: quelle.kunden_id ?? doc.kunden_id,
        ansprechpartner_id: quelle.ansprechpartner_id ?? doc.ansprechpartner_id,
        betreff: quelle.betreff ?? doc.betreff,
        kunde_name: quelle.kunde_name ?? doc.kunde_name,
        kunde_strasse: quelle.kunde_strasse ?? doc.kunde_strasse,
        kunde_plz: quelle.kunde_plz ?? doc.kunde_plz,
        kunde_ort: quelle.kunde_ort ?? doc.kunde_ort,
        kunde_email: quelle.kunde_email ?? doc.kunde_email,
        kunde_telefon: quelle.kunde_telefon ?? doc.kunde_telefon,
        kundennummer: quelle.kundennummer ?? doc.kundennummer,
        ansprechpartner_name: quelle.ansprechpartner_name ?? doc.ansprechpartner_name,
        ansprechpartner_email: quelle.ansprechpartner_email ?? doc.ansprechpartner_email,
        ansprechpartner_telefon: quelle.ansprechpartner_telefon ?? doc.ansprechpartner_telefon,
      };
      // Rechnungen: Rechnungsadresse überschreibt nur die Anschrift – Ansprechpartner bleibt.
      if (doc.dokument_typ === "rechnung") {
        const addrs = doc.rechnungsadressen || [];
        const chosen =
          (doc.rechnungsadresse_id != null
            ? addrs.find((a) => a.id === doc.rechnungsadresse_id)
            : null) ||
          addrs.find((a) => a.is_default) ||
          addrs[0] ||
          null;
        if (chosen) {
          Object.assign(patch, applyRechnungsadresseToEmpfaenger(chosen));
        }
      }
      patchDoc(patch);
      toast.success("Kopfdaten aus Projekt übernommen.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kopfdaten konnten nicht übernommen werden.");
    } finally {
      setImportingHeader(false);
    }
  }

  const subtitle = doc ? (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span
        className={clsx(
          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
          doc.dokument_typ === "rechnung" &&
            (doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet")
            ? DOKUMENT_STATUS_BADGE.abgerechnet
            : doc.dokument_typ === "rechnung" && doc.zahlungsstatus === "teilbezahlt"
              ? "bg-status-orange/15 text-status-orange"
              : DOKUMENT_STATUS_BADGE[doc.status],
        )}
      >
        {doc.dokument_typ === "rechnung" &&
        (doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet")
          ? "Bezahlt"
          : doc.dokument_typ === "rechnung" && doc.zahlungsstatus === "teilbezahlt"
            ? "Teilbezahlt"
            : DOKUMENT_STATUS_LABELS[doc.status]}
      </span>
      <span>{getDokumentTypLabel(doc)}</span>
      {doc.dokument_typ === "rechnung" && doc.status !== "entwurf" ? (
        <span className="text-xs text-label-secondary">
          · {doc.hat_zugferd_pdf ? "ZUGFeRD" : "PDF"}
          {doc.hat_xrechnung_xml ? " + X-Rechnung" : ""}
        </span>
      ) : null}
      {doc.customer_signed_at ? (
        <span className="text-xs text-status-green">
          · Kunde unterschrieben {new Date(doc.customer_signed_at).toLocaleDateString("de-DE")}
        </span>
      ) : null}
      {doc.kunde_name ? <span>· {doc.kunde_name}</span> : null}
      {doc.auftrag_nummer ? <span>· {doc.auftrag_nummer}</span> : null}
      {doc.hero_document_id ? (
        <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label-secondary">
          Historisch (Hero)
        </span>
      ) : null}
      {doc.dokument_typ === "rechnung" && doc.auftrag_id ? (
        <InvoiceDraftContextButtons
          auftragId={doc.auftrag_id}
          docId={doc.id}
          chain={chain}
          buttonClassName="h-7 w-7 shrink-0"
        />
      ) : doc.auftrag_id ? (
        <ProjectOverviewButton auftragId={doc.auftrag_id} className="h-7 w-7 shrink-0" />
      ) : null}
    </span>
  ) : null;

  const billingQuestionActions = (
    <>
      {canCreateBillingQ && viewingOwnDocument && editable ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setBillingCreateOpen(true)}
          disabled={saving || isAutoSaving}
          title="Rückfrage an die Geschäftsführung stellen"
        >
          Rückfrage
        </Button>
      ) : null}
      {showBillingAnswersButton && viewingOwnDocument ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setBillingAnswersOpen(true)}
          disabled={saving}
          title="Rückfragen und Antworten anzeigen"
        >
          Rückfragen anzeigen
        </Button>
      ) : null}
    </>
  );

  const actionBar = doc ? (
    <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-separator pb-4">
      {isRechnung && pdfView ? (
        <SegmentedControl
          name="Rechnungsansicht"
          className="mr-auto"
          value={invoiceDetailView}
          onChange={(value) => {
            setInvoiceDetailView(value);
            if (value === "pdf") closePositionEditor();
          }}
          options={[
            { value: "pdf", label: "PDF" },
            { value: "kalkulation", label: "Kalkulation" },
          ]}
        />
      ) : null}
      {linkedAga ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setAgaDialogOpen(true)}
          title="Zugehörige Angebotsanfrage ansehen"
        >
          {linkedAga.aga_nummer
            ? `Angebotsanfrage ${linkedAga.aga_nummer}`
            : "Angebotsanfrage"}
        </Button>
      ) : null}
      {pdfView && canReopen ? (
        <Button type="button" variant="primary" onClick={() => setReopenConfirmOpen(true)} disabled={saving}>
          Erneut bearbeiten
        </Button>
      ) : null}
      {canDraftFromCancelled ? (
        <Button
          type="button"
          variant="primary"
          onClick={() => setDraftFromCancelledConfirmOpen(true)}
          disabled={saving}
        >
          Neuen Rechnungsentwurf erstellen
        </Button>
      ) : null}
      {canRepairStorno ? (
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleRepairStornoPositionen()}
          disabled={saving}
          title="Positionen aus der Quellrechnung bzw. Hero nachladen – Rechnungsnummer bleibt gleich"
        >
          Positionen nachziehen
        </Button>
      ) : null}
      {editable && !isDraft ? (
        <Button type="button" variant="secondary" onClick={() => setPreviewOpen(true)}>
          Vorschau
        </Button>
      ) : null}
      {isDraft && editable ? null : billingQuestionActions}
      {editable && doc.dokument_typ === "angebot" ? (
            <Button
              type="button"
              variant="secondary"
              disabled={saving || isAutoSaving}
              onClick={() => {
                void (async () => {
                  try {
                    const withKeys = positions.map((row) => {
                      const typ = normalizePositionTyp(row.position_typ);
                      if (typ === "titel" || typ === "text") return row;
                      return ensurePositionMediaKey(row);
                    });
                    flushSync(() => setPositions(withKeys));
                    const saved = await flushSave();
                    if (!saved) {
                      throw new Error(
                        "Entwurf konnte nicht gespeichert werden – Handy-Upload nicht gestartet.",
                      );
                    }
                    setMobileUploadOpen(true);
                  } catch (err) {
                    setError(
                      err instanceof ApiError
                        ? err.message
                        : err instanceof Error
                          ? err.message
                          : "Speichern vor Handy-Upload fehlgeschlagen.",
                    );
                  }
                })();
              }}
            >
              Bilder vom Handy
            </Button>
      ) : null}
      {pdfView ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => void handleOpenPdf()}
          disabled={!pdfEnabled || pdfLoading}
          loading={pdfLoading}
        >
          {activeChainStep?.kind === "kalkulation"
            ? "Kalkulation herunterladen"
            : activeChainStep?.kind === "beauftragung"
              ? "Beauftragung herunterladen"
              : activeChainStep?.kind === "mahnung"
                ? "Mahnung herunterladen"
              : activeChainStep?.kind === "lieferschein" && activeChainStep.has_signed_pdf
                ? "Unterschriebenes PDF herunterladen"
                : doc.dokument_typ === "rechnung"
                  ? "ZUGFeRD-PDF herunterladen"
                  : "PDF herunterladen"}
        </Button>
      ) : null}
      {printTarget ? (
        <DokumentPrintAction
          docId={printTarget.docId}
          variant={printTarget.variant}
          disabled={pdfLoading}
          zIndexClass={layers.overlay}
        />
      ) : null}
      {canSendDokumentMail(doc) ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setMailOpen(true)}
          disabled={saving}
        >
          E-Mail
        </Button>
      ) : null}
      {doc.dokument_typ === "rechnung" && doc.hat_xrechnung_xml ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => void handleOpenXrechnung()}
          disabled={pdfLoading}
          loading={pdfLoading}
        >
          X-Rechnung herunterladen
        </Button>
      ) : null}
      {doc.dokument_typ === "rechnung" &&
      (doc.status === "erstellt" || doc.status === "versendet" || doc.status === "abgerechnet") ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setPaymentsOpen(true)}
          disabled={saving}
        >
          {doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet"
            ? "Zahlungen"
            : doc.zahlungsstatus === "teilbezahlt"
              ? "Teilzahlung / Zahlung"
              : "Zahlung erfassen"}
        </Button>
      ) : null}
      {canBillingReopen ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setBillingReopenOpen(true)}
          disabled={saving}
          title="Als Teilrechnung oder Vorkasse markieren und Fertigstellung aufheben"
        >
          Als Teil-/Vorkasse markieren
        </Button>
      ) : null}
      {doc.status === "erstellt" ? (
        <>
          <Button type="button" variant="secondary" onClick={() => void handleStatus("versendet")} disabled={saving}>
            Als versendet markieren
          </Button>
          {doc.rechnung_art !== "stornorechnung" ? (
            <Button type="button" variant="danger" onClick={() => void handleStatus("storniert")} disabled={saving}>
              Stornieren
            </Button>
          ) : null}
        </>
      ) : null}
      {!editable && doc.status !== "storniert" && doc.status !== "erstellt" ? (
        <>
          {doc.status === "versendet" && doc.dokument_typ === "angebot" ? (
            <>
              <Button type="button" variant="secondary" onClick={() => void handleStatus("angenommen")} disabled={saving}>
                Angenommen
              </Button>
              <Button type="button" variant="secondary" onClick={() => void handleStatus("abgelehnt")} disabled={saving}>
                Abgelehnt
              </Button>
            </>
          ) : null}
          {doc.status === "versendet" && doc.dokument_typ === "lieferschein" ? (
            <Button type="button" variant="secondary" onClick={() => void handleStatus("geliefert")} disabled={saving}>
              Geliefert
            </Button>
          ) : null}
          {doc.rechnung_art !== "stornorechnung" ? (
            <Button type="button" variant="danger" onClick={() => void handleStatus("storniert")} disabled={saving}>
              Stornieren
            </Button>
          ) : null}
        </>
      ) : null}
      {canCreateAuftragsbestaetigungFromAngebot ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => openAuftragsbestaetigungFromAngebot({ angebotId: doc.id })}
          disabled={saving}
        >
          Auftragsbestätigung erstellen
        </Button>
      ) : null}
      {conversionOptions.map((target) => (
        <Button
          key={target}
          type="button"
          variant="secondary"
          onClick={() => void handleConvert(target)}
          disabled={saving}
        >
          → {DOKUMENT_TYP_LABELS[target]}
        </Button>
      ))}
    </div>
  ) : null;

  let body: ReactNode;
  if (docId == null || !Number.isFinite(docId)) {
    body = <Alert variant="error">Ungültige Dokument-ID.</Alert>;
  } else if (loading) {
    body = (
      <div className="flex items-center gap-2 py-8 text-sm text-label-secondary">
        <Spinner />
        Dokument wird geladen …
      </div>
    );
  } else if (!doc) {
    body = <Alert variant="error">{error || "Dokument nicht gefunden."}</Alert>;
  } else if (pdfView && isRechnung && invoiceDetailView === "kalkulation") {
    body = (
      <>
        {actionBar}
        {error ? <Alert variant="error">{error}</Alert> : null}
        <Alert variant="info" className="mb-4">
          Gespeicherte Kalkulation dieser Rechnung — nur zur Ansicht. Zeile anklicken, um
          Artikeldetails und Kalkulation zu öffnen.
        </Alert>
        <DocumentPositionCanvas
          sidebarPlacement="external"
          positions={positions}
          editable={false}
          currency={doc.waehrung}
          lohnarten={visibleLohnarten}
          dokumentTyp={doc.dokument_typ}
          templates={templates}
          templatesLoading={templatesLoading}
          applyingTemplateId={applyingTemplateId}
          onChange={() => undefined}
          onOpenPosition={openPosition}
          onApplyTemplate={() => undefined}
          onDeleteTemplate={() => undefined}
          onSaveTemplate={() => undefined}
          auftragId={doc.auftrag_id ?? null}
          kundenId={doc.kunden_id ?? null}
          kundeName={doc.kunde_name ?? null}
          docId={doc.id}
          attachmentsRevision={attachmentsRevision}
          billingBadgeByLocalId={billingBadgeByLocalId}
        >
          <DocumentPositionCanvasPane />
        </DocumentPositionCanvas>
      </>
    );
  } else if (pdfView && chain) {
    body = (
      <>
        {actionBar}
        {error ? <Alert variant="error">{error}</Alert> : null}
        {exc2KalkulationHinweis}
        {chainPanel}
        {doc.dokument_typ === "rechnung" &&
        (doc.status === "erstellt" || doc.status === "versendet" || doc.status === "abgerechnet") ? (
          <DocumentMahnungPanel
            docId={doc.id}
            enabled
            zIndexClass={layers.overlay}
            onChanged={() => void reload({ silent: true })}
          />
        ) : null}
      </>
    );
  } else if (pdfView) {
    body = (
      <>
        {actionBar}
        {error ? <Alert variant="error">{error}</Alert> : null}
        {exc2KalkulationHinweis}
        {doc.has_newer_revision ? (
          <Alert variant="info">
            Es gibt eine neuere Version dieses Angebots. Diese Ansicht bleibt zur Nachvollziehbarkeit
            erhalten.
          </Alert>
        ) : null}
        <DocumentPdfEmbed
          docId={doc.id}
          variant={
            doc.dokument_typ === "lieferschein" && doc.has_signed_pdf ? "signed" : "angebot"
          }
          zIndexClass={layers.overlay}
        />
        {doc.dokument_typ === "rechnung" &&
        (doc.status === "erstellt" || doc.status === "versendet" || doc.status === "abgerechnet") ? (
          <DocumentMahnungPanel
            docId={doc.id}
            enabled
            zIndexClass={layers.overlay}
            onChanged={() => void reload({ silent: true })}
          />
        ) : null}
      </>
    );
  } else if (editable && !viewingOwnDocument && showChainTabs && chain) {
    body = (
      <>
        {actionBar}
        {error ? <Alert variant="error">{error}</Alert> : null}
        {exc2KalkulationHinweis}
        {chainPanel}
      </>
    );
  } else if (editable) {
    body = (
      <>
        {actionBar}
        {error ? <Alert variant="error">{error}</Alert> : null}
        {requiresExc2Answer && !exc2Loading ? (
          <Alert variant="warning" className="mb-4">
            Bitte unter Kopfdaten angeben, ob es ein EXC2-Projekt ist — erst danach kann das
            Angebot erstellt werden.
          </Alert>
        ) : null}
        {billingUpdateCount > 0 && editable ? (
          <Alert variant="warning" className="mb-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {billingUpdateCount === 1
                  ? "1 Position hat eine neuere Rückfragen-Antwort mit anderen Unterpositionen."
                  : `${billingUpdateCount} Positionen haben neuere Rückfragen-Antworten mit anderen Unterpositionen.`}
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="shrink-0"
                onClick={() => setBillingAnswersOpen(true)}
              >
                Antworten prüfen
              </Button>
            </div>
          </Alert>
        ) : null}
        <DocumentPositionCanvas
          sidebarPlacement="external"
          positions={positions}
          editable={editable}
          currency={doc.waehrung}
          lohnarten={visibleLohnarten}
          dokumentTyp={doc.dokument_typ}
          templates={templates}
          templatesLoading={templatesLoading}
          applyingTemplateId={applyingTemplateId}
          onChange={setPositions}
          onOpenPosition={openPosition}
          onApplyTemplate={(templateId, replace, insertAt) =>
            void handleApplyTemplate(templateId, replace, insertAt)
          }
          onDeleteTemplate={handleDeleteTemplate}
          onEditTemplate={handleEditTemplate}
          onReorderTemplates={(ids) => void handleReorderTemplates(ids)}
          onSaveTemplate={() => setSaveTemplateOpen(true)}
          auftragId={doc.auftrag_id ?? null}
          kundenId={doc.kunden_id ?? null}
          kundeName={doc.kunde_name ?? null}
          docId={doc.id}
          attachmentsRevision={attachmentsRevision}
          billingBadgeByLocalId={billingBadgeByLocalId}
        >
          {showInvoiceContext && doc.auftrag_id != null ? (
            <DocumentInvoiceContextPanel
              auftragId={Number(doc.auftrag_id)}
              auftragsnummer={doc.auftrag_nummer}
            />
          ) : null}
          <div className="gd-editor-pane space-y-4">
              <DocumentSaveStatus
                saveState={saveState}
                hasUnsavedChanges={hasUnsavedChanges}
                lastSavedAt={lastSavedAt}
                saveError={saveError}
                onRetry={() => void retrySave()}
                retrying={saveState === "saving"}
              />

              <DocumentHeaderSection
                doc={doc}
                editable={editable}
                importing={importingHeader}
                onEditSection={setHeaderSection}
                onImportFromProject={() => void handleImportHeaderFromProject()}
                chain={chain}
                exc2={
                  doc.dokument_typ === "angebot" && doc.auftrag_id
                    ? {
                        loading: exc2Loading,
                        saving: exc2Saving,
                        projectIsExc2,
                        choice: exc2Choice,
                        onChange: (choice) => void handleExc2Choice(choice),
                      }
                    : null
                }
              />

              {doc.abschlag_modus === "prozent" && doc.abschlag_prozent != null ? (
                <Alert variant="info" className="mb-4">
                  Abschlagsrechnung: {String(doc.abschlag_prozent).replace(".", ",")} % der
                  Angebotspositionen (inkl. Projektnachlass). Die Gesamtsumme betrifft nur diesen
                  Abschlag.
                </Alert>
              ) : null}
              {doc.abschlag_modus === "festbetrag" && doc.abschlag_betrag_netto != null ? (
                <Alert variant="info" className="mb-4">
                  Abschlagsrechnung mit Festbetrag{" "}
                  {Number(doc.abschlag_betrag_netto).toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  € netto (anteilige Angebotspositionen inkl. Projektnachlass).
                </Alert>
              ) : null}

              <DocumentIntroSection
                doc={doc}
                editable={editable}
                onEdit={() => setBodyTextKind("einleitung")}
              />

              <DocumentPositionCanvasPane />

              <div className="rounded-xl border border-separator bg-card p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-label-secondary">Zwischensumme netto</span>
                    <span>{formatCurrency(previewTotals.summe_netto, doc.waehrung)}</span>
                  </div>
                  {taxGroups.map((g) => (
                    <div key={g.rate} className="flex justify-between">
                      <span className="text-label-secondary">MwSt. {g.rate}%</span>
                      <span>{formatCurrency(g.tax, doc.waehrung)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-separator pt-2 text-base font-semibold">
                    <span>Gesamt brutto</span>
                    <span>{formatCurrency(previewTotals.summe_brutto, doc.waehrung)}</span>
                  </div>
                </div>
              </div>

              <DocumentFooterSection
                doc={doc}
                editable={editable}
                onEdit={() => setBodyTextKind("schlusstext")}
              />

              <DocumentVerknuepfungenBlock
                doc={doc}
                editable={editable}
                onEdit={() => setReferenzenOpen(true)}
              />
          </div>
          <DocumentPositionCanvasSidebar />
        </DocumentPositionCanvas>
      </>
    );
  } else {
    body = (
      <>
        {actionBar}
        {error ? <Alert variant="error">{error}</Alert> : null}
        {exc2KalkulationHinweis}
        <Alert variant="info">
          {doc.status === "storniert"
            ? "Dieses Dokument wurde storniert."
            : "Dieses Dokument kann in diesem Status nicht bearbeitet werden."}
        </Alert>
        {showChainTabs && chain ? chainPanel : null}
        {doc.dokument_typ === "rechnung" ? (
          <div className="mt-3 rounded-xl border border-separator bg-card p-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold text-label">Zahlungsstatus</h3>
              <span
                className={clsx(
                  "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                  doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet"
                    ? "bg-status-green/15 text-status-green"
                    : doc.zahlungsstatus === "teilbezahlt"
                      ? "bg-status-orange/15 text-status-orange"
                      : "bg-fill-tertiary text-label-secondary",
                )}
              >
                {doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet"
                  ? "Bezahlt"
                  : doc.zahlungsstatus === "teilbezahlt"
                    ? "Teilbezahlt"
                    : "Offen"}
              </span>
            </div>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between">
                <span className="text-label-secondary">Brutto</span>
                <span
                  className={clsx(
                    doc.zahlungsstatus === "bezahlt" || doc.status === "abgerechnet"
                      ? "font-semibold text-status-green"
                      : undefined,
                  )}
                >
                  {formatCurrency(doc.summe_brutto, doc.waehrung)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-label-secondary">Bezahlt</span>
                <span
                  className={clsx(
                    (doc.summe_bezahlt || 0) > 0 ? "text-status-green" : undefined,
                  )}
                >
                  {formatCurrency(doc.summe_bezahlt || 0, doc.waehrung)}
                </span>
              </div>
              <div className="flex justify-between border-t border-separator pt-1 font-semibold">
                <span>Offen</span>
                <span
                  className={clsx(
                    (doc.summe_offen ?? doc.summe_brutto) <= 0
                      ? "text-status-green"
                      : "text-status-orange",
                  )}
                >
                  {formatCurrency(doc.summe_offen ?? doc.summe_brutto, doc.waehrung)}
                </span>
              </div>
            </div>
          </div>
        ) : null}
        {doc.dokument_typ === "rechnung" &&
        (doc.status === "erstellt" || doc.status === "versendet" || doc.status === "abgerechnet") ? (
          <DocumentMahnungPanel
            docId={doc.id}
            enabled
            zIndexClass={layers.overlay}
            onChanged={() => void reload({ silent: true })}
          />
        ) : null}
        <div className="mt-3">
          <DocumentVerknuepfungenBlock
            doc={doc}
            editable={false}
            onEdit={() => undefined}
          />
        </div>
      </>
    );
  }

  return (
    <InvoiceDraftContextModalsProvider chain={chain} docId={doc?.id ?? null}>
    <>
      <Dialog
        open={open}
        onClose={saving || isAutoSaving ? () => undefined : onClose}
        title={doc ? dokumentDisplayTitle(doc) : "Dokument"}
        description={subtitle}
        size="4xl"
        fullScreenMobile
        dirty={closeGuardDirty}
        zIndexClass={layers.editor}
        onActivate={() => focusSurface("documentEditor")}
        panelClassName={
          isRechnung
            ? "max-h-[95vh] w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)]"
            : "max-h-[95vh] w-[min(100vw-1rem,96rem)] max-w-[min(100vw-1rem,96rem)]"
        }
        bodyClassName="overflow-y-auto"
        footer={
          isDraft && editable ? (
            <>
              <span className="mr-auto inline-flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="!text-status-red hover:bg-status-red/10"
                  onClick={() => void handleDelete()}
                  disabled={saving || isAutoSaving}
                >
                  Entwurf löschen
                </Button>
                {billingQuestionActions}
              </span>
              <Button type="button" variant="secondary" onClick={() => setPreviewOpen(true)}>
                Vorschau
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  openMailAfterCreateRef.current = false;
                  void handleStatus("erstellt");
                }}
                disabled={saving || isAutoSaving || requiresExc2Answer}
                title={
                  requiresExc2Answer
                    ? "Bitte zuerst unter Kopfdaten die EXC2-Frage beantworten."
                    : undefined
                }
              >
                Erstellen
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => void handleCreateAndSend()}
                disabled={saving || isAutoSaving || requiresExc2Answer}
                title={
                  requiresExc2Answer
                    ? "Bitte zuerst unter Kopfdaten die EXC2-Frage beantworten."
                    : "Dokument erstellen und E-Mail-Versand öffnen"
                }
              >
                Erstellen und Versenden
              </Button>
            </>
          ) : undefined
        }
      >
        {body}
      </Dialog>

      {doc ? (
        <>
          <DocumentHeaderModal
            open={headerSection != null}
            section={headerSection}
            onClose={() => setHeaderSection(null)}
            doc={doc}
            editable={editable}
            onChange={patchDoc}
            onResetCreatorContact={() => void handleResetCreatorContact()}
            onEditReferenzen={() => {
              setHeaderSection(null);
              setReferenzenOpen(true);
            }}
            zIndexClass={layers.overlay}
          />

          <DocumentReferenzenDialog
            open={referenzenOpen}
            onClose={() => setReferenzenOpen(false)}
            doc={doc}
            editable={editable}
            onApplied={(updated) => handleSaved(updated)}
            zIndexClass={layers.overlay}
          />

          <DocumentBodyTextModal
            open={bodyTextKind != null}
            kind={bodyTextKind}
            onClose={() => setBodyTextKind(null)}
            doc={doc}
            editable={editable}
            onChange={patchDoc}
            zIndexClass={layers.overlay}
          />

          <DocumentBillingQuestionCreateDialog
            open={billingCreateOpen}
            onClose={() => setBillingCreateOpen(false)}
            dokumentId={doc.id}
            positions={positions}
            zIndexClass={layers.overlay}
            onCreated={() => void reloadBillingQuestions()}
          />

          <DocumentBillingAnswersDialog
            open={billingAnswersOpen}
            onClose={() => setBillingAnswersOpen(false)}
            dokumentId={doc.id}
            dokumentQuestions={dokumentBillingQuestions}
            timesheetQuestions={timesheetBillingQuestions}
            timesheetMaterials={timesheetBillingMaterials}
            positions={positions}
            editable={editable}
            zIndexClass={layers.overlay}
            onApplyChildren={applyBillingChildren}
            onReloadQuestions={() => void reloadBillingQuestions()}
          />

          <DocumentPreviewDialog
            open={previewOpen}
            doc={doc}
            positions={positions}
            onClose={() => setPreviewOpen(false)}
            zIndexClass={layers.overlay}
            showCreateActions={Boolean(isDraft && editable)}
            createDisabled={saving || isAutoSaving || requiresExc2Answer}
            createDisabledReason={
              requiresExc2Answer
                ? "Bitte zuerst unter Kopfdaten die EXC2-Frage beantworten."
                : undefined
            }
            creating={saving}
            onCreate={() => {
              openMailAfterCreateRef.current = false;
              return handleStatus("erstellt");
            }}
            onCreateAndSend={async () => {
              openMailAfterCreateRef.current = true;
              const ok = await handleStatus("erstellt");
              if (ok) {
                openMailAfterCreateRef.current = false;
                setMailOpen(true);
              }
              return ok;
            }}
          />

          {doc.dokument_typ === "rechnung" ? (
            <DocumentPaymentsDialog
              open={paymentsOpen}
              doc={doc}
              onClose={() => setPaymentsOpen(false)}
              zIndexClass={layers.overlay}
              onUpdated={(next) => {
                setDoc(next);
                setSavedSnapshot(buildEditorSnapshot(next, positions));
              }}
            />
          ) : null}

          {mailOpen ? (
            <DocumentMailDialog
              open
              docId={doc.id}
              defaultMarkVersendet={doc.status === "erstellt"}
              zIndexClass={layers.overlay}
              onClose={() => setMailOpen(false)}
              onSent={async () => {
                toast.success("E-Mail wurde versendet.");
                setMailOpen(false);
                await reload({ silent: true });
              }}
              onError={(message) => toast.error(message)}
            />
          ) : null}

          {doc.dokument_typ === "rechnung" ? (
            <BillingReopenDialog
              open={billingReopenOpen}
              subject={{
                source: "gd",
                id: doc.id,
                label: doc.dokument_nummer || "Rechnung",
                auftragId: doc.auftrag_id,
                auftragNummer: doc.auftrag_nummer,
                currentArt: doc.rechnung_art,
              }}
              onClose={() => setBillingReopenOpen(false)}
              zIndexClass={layers.overlay}
              onDone={() => {
                void reload({ silent: true });
              }}
            />
          ) : null}

          {editPosition && isMergedContainerPosition(editPosition) ? (
            <DocumentMergedPositionEditDialog
              open={editLocalId != null}
              position={editPosition}
              editable={editable}
              currency={doc.waehrung}
              lohnarten={visibleLohnarten}
              kundenId={doc.kunden_id ?? null}
              auftragId={doc.auftrag_id ?? null}
              dokumentTyp={doc.dokument_typ}
              onClose={closePositionEditor}
              zIndexClass={layers.overlay}
              onSave={savePosition}
              onExtractChild={(mergedPosition, childIndex) => {
                let updatedMerged: EditorPosition | null = null;
                setPositions((prev) => {
                  const withDraft = prev.map((p) =>
                    p.localId === mergedPosition.localId ? mergedPosition : p,
                  );
                  const next = extractMergedChildFromPositions(
                    withDraft,
                    mergedPosition.localId,
                    childIndex,
                  );
                  const found = next.find((p) => p.localId === mergedPosition.localId);
                  updatedMerged =
                    found && isMergedContainerPosition(found) ? found : null;
                  return next;
                });
                return updatedMerged;
              }}
            />
          ) : (
            <DocumentPositionEditDialog
              open={editLocalId != null}
              position={editPosition}
              editable={editable}
              currency={doc.waehrung}
              lohnarten={visibleLohnarten}
              kundenId={doc.kunden_id ?? null}
              auftragId={doc.auftrag_id ?? null}
              docId={doc.id}
              dokumentTyp={doc.dokument_typ}
              onClose={closePositionEditor}
              zIndexClass={layers.overlay}
              onAttachmentsChanged={() => setAttachmentsRevision((n) => n + 1)}
              onSave={savePosition}
            />
          )}

          <DocumentMobileUploadDialog
            open={mobileUploadOpen}
            onClose={() => {
              setMobileUploadOpen(false);
              setAttachmentsRevision((n) => n + 1);
            }}
            docId={doc.id}
            zIndexClass={layers.overlay}
          />

          <ExistingLieferscheinDialog
            open={lieferscheinConflictOpen}
            busy={saving}
            items={existingLieferscheine}
            zIndexClass={layers.nested}
            onClose={() => {
              if (saving) return;
              setLieferscheinConflictOpen(false);
            }}
            onContinue={() => void handleConvert("lieferschein", { forceNew: true })}
            onOpenExisting={(id) => {
              setLieferscheinConflictOpen(false);
              onDocIdChange?.(id);
            }}
          />

          <ConfirmActionDialog
            open={zeroEuroWarningOpen}
            title="Positionen mit 0 €"
            confirmLabel="Akzeptieren und erstellen"
            cancelLabel="Zurück zum Entwurf"
            variant="warning"
            loading={saving}
            zIndexClass={layers.nested}
            onConfirm={() => void confirmZeroEuroCreate()}
            onCancel={cancelZeroEuroCreate}
          >
            <p className="mb-3">
              Folgende Positionen haben einen Betrag von {formatCurrency(0, doc.waehrung)}.
              Möchten Sie die Rechnung trotzdem erstellen?
            </p>
            <ul className="max-h-56 space-y-1.5 overflow-y-auto rounded-ios border border-separator bg-card-elevated px-3 py-2">
              {zeroEuroWarningPositions.map((pos) => (
                <li key={pos.localId} className="flex gap-2 text-sm text-label">
                  <span className="shrink-0 font-medium tabular-nums text-label-secondary">
                    {pos.displayNumber ? `${pos.displayNumber}.` : "–"}
                  </span>
                  <span className="min-w-0 break-words">{pos.title}</span>
                  <span className="ml-auto shrink-0 tabular-nums text-label-secondary">
                    {formatCurrency(0, doc.waehrung)}
                  </span>
                </li>
              ))}
            </ul>
          </ConfirmActionDialog>

          <ConfirmActionDialog
            open={reopenConfirmOpen}
            title={
              doc && usesDokumentRevisionForReopen(doc)
                ? "Neue Angebots-Version anlegen?"
                : "Dokument erneut bearbeiten?"
            }
            confirmLabel={
              doc && usesDokumentRevisionForReopen(doc) ? "Version anlegen" : "Entwurf öffnen"
            }
            loading={saving}
            zIndexClass={layers.overlay}
            onConfirm={() => void confirmReopenForEdit()}
            onCancel={() => setReopenConfirmOpen(false)}
          >
            {doc && usesDokumentRevisionForReopen(doc) ? (
              <>
                Es wird eine neue Version von {dokumentDisplayNumber(doc)} als Entwurf angelegt (mit
                Suffix .1, .2, …). Die bisherige Version bleibt unverändert einsehbar.
              </>
            ) : (
              <>
                Das Dokument wird wieder in den Entwurf gesetzt. Anschließend können Sie es bearbeiten
                und erneut erstellen.
              </>
            )}
          </ConfirmActionDialog>

          <ConfirmActionDialog
            open={draftFromCancelledConfirmOpen}
            title="Neuen Rechnungsentwurf erstellen?"
            confirmLabel="Entwurf anlegen"
            loading={saving}
            zIndexClass={layers.overlay}
            onConfirm={() => void confirmDraftFromCancelled()}
            onCancel={() => setDraftFromCancelledConfirmOpen(false)}
          >
            Es wird ein neuer Rechnungsentwurf mit den Positionen und Kopfdaten der stornierten
            Rechnung angelegt. Verknüpfte Stundennachweise und Lieferscheine werden auf den neuen
            Entwurf umgehängt. Storno-Belege bleiben unverändert.
          </ConfirmActionDialog>

          <DocumentSaveTemplateDialog
            open={saveTemplateOpen}
            onClose={() => setSaveTemplateOpen(false)}
            doc={doc}
            positions={positions}
            zIndexClass={layers.overlay}
            onSaved={() => void reloadTemplates()}
          />

          <DocumentTemplateEditorDialog
            open={editTemplateId != null}
            templateId={editTemplateId}
            onClose={() => setEditTemplateId(null)}
            onSaved={() => void reloadTemplates()}
            zIndexClass={layers.overlay}
          />

          <ConfirmActionDialog
            open={deleteTemplateId != null}
            title="Vorlage löschen?"
            confirmLabel="Löschen"
            variant="danger"
            zIndexClass={layers.overlay}
            onConfirm={() => void confirmDeleteTemplate()}
            onCancel={() => setDeleteTemplateId(null)}
          >
            Diese Vorlage wird dauerhaft entfernt und steht danach nicht mehr in der Liste.
          </ConfirmActionDialog>
        </>
      ) : null}

      <AngebotDialog
        open={agaDialogOpen && linkedAga != null}
        agaId={linkedAga?.id ?? null}
        canProcess={canProcessAga}
        zIndexClass={layers.aga}
        overlayZIndexClass={layers.agaOverlay}
        onClose={() => setAgaDialogOpen(false)}
        onChanged={() => {
          if (!doc || doc.dokument_typ !== "angebot") return;
          void getAngebotsanfrageByGdAngebot(doc.id)
            .then(setLinkedAga)
            .catch(() => setLinkedAga(null));
        }}
      />

      <AngebotAngenommenDialog
        open={angenommenDialogOpen && doc?.dokument_typ === "angebot"}
        angebot={doc?.dokument_typ === "angebot" ? doc : null}
        zIndexClass={layers.ab}
        onClose={() => setAngenommenDialogOpen(false)}
        onAccepted={applyAcceptedAngebot}
      />
    </>
    </InvoiceDraftContextModalsProvider>
  );
}
